import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddNewQualification from "./AddNewQualification";

// Styled components
const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
}));

function Qualifications() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedQualification, setSelectedQualification] = useState(null);
  const [openAddNewQualification, setOpenAddNewQualification] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 5;
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  useEffect(() => {
    fetchQualifications();
  }, []);

  const fetchQualifications = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/qualifications");
      const data = await res.json();
      const list = data.recordset || data;
      setQualifications(list);
    } catch (error) {
      console.error("Error fetching qualifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  // 🔍 Search filter
  const filteredQualifications = qualifications.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.Qualification?.toLowerCase().includes(search) ||
      item.Degree?.toLowerCase().includes(search)
    );
  });

  const sortedQualifications = [...filteredQualifications].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedQualifications.slice(indexOfFirstItem, indexOfLastItem);

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedQualification(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedQualification(null);
  };

  return (
    <Box sx={{ backgroundColor: "white", borderRadius: 2, p: 3, mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Qualifications
        </Typography>

        <TextField
          size="small"
          placeholder="Search by Qualification or Degree..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddNewQualification(true)}
        >
          Add New
        </Button>
      </Box>

      {/* Table */}
      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.primary.light }}>
              {[
                { id: "no", label: "No." },
                { id: "Qualification", label: "Qualification" },
                { id: "QualificationField", label: "Qualification Field" },
                { id: "Degree", label: "Degree" },
                { id: "actions", label: "Action" },
              ].map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() => column.id !== "actions" && handleSort(column.id)}
                  sx={{ cursor: column.id !== "actions" ? "pointer" : "default", color: "white" }}
                >
                  {column.label}
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((item, index) => {
                const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;

                return (
                  <StyledTableRow key={item.id}>
                    <ModernTableCell>{rowNumber}</ModernTableCell>
                    <ModernTableCell>{item.Qualification}</ModernTableCell>
                    <ModernTableCell>{item.QualificationField}</ModernTableCell>
                    <ModernTableCell>{item.Degree}</ModernTableCell>
                    <ModernTableCell>
                      <Tooltip title="More actions" arrow>
                        <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </ModernTableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <TableRow>
                <ModernTableCell colSpan={5} align="center">
                  No qualifications found
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      <Pagination
        sx={{ mt: 2, display: "flex", justifyContent: "center" }}
        count={Math.ceil(filteredQualifications.length / itemsPerPage)}
        page={currentPage}
        onChange={(e, value) => setCurrentPage(value)}
        color="primary"
      />

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <DeleteIcon fontSize="small" color="error" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add Modal */}
      <AddNewQualification
        open={openAddNewQualification}
        onClose={() => setOpenAddNewQualification(false)}
        onSaveSuccess={fetchQualifications}
      />
    </Box>
  );
}

export default Qualifications;
